/**
 * Maintenance Tasks tab: filterable catalog of available task classes.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Search, RefreshCw } from 'lucide-react';
import type { MtEntry, Pagination } from './types';
import { Pager } from './helpers';

interface MaintenanceTabProps {
  mtEntries: MtEntry[];
  mtLoading: boolean;
  mtLoaded: boolean;
  mtSearch: string;
  setKbSearch: (v: string) => void;
  mtPage: number;
  setMtPage: React.Dispatch<React.SetStateAction<number>>;
  filteredMts: MtEntry[];
  pagedMts: MtEntry[];
  mtPagination: Pagination;
}

export function MaintenanceTab({
  mtEntries,
  mtLoading,
  mtLoaded,
  mtSearch,
  setKbSearch,
  mtPage,
  setMtPage,
  filteredMts,
  pagedMts,
  mtPagination,
}: MaintenanceTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={mtSearch}
            onChange={(e) => {
              setKbSearch(e.target.value);
              setMtPage(0);
            }}
            placeholder="Filter maintenance tasks…"
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        {mtLoaded && (
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {filteredMts.length === mtEntries.length
              ? `${mtEntries.length} tasks`
              : `${filteredMts.length} of ${mtEntries.length} tasks`}
          </span>
        )}
      </div>

      {mtLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
          {filteredMts.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">No entries found.</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                  {pagedMts.map((entry) => (
                    <tr
                      key={entry.taskClass}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/maintenance-tasks/${entry.taskClass}`}
                          className="font-medium text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                        >
                          {entry.displayName}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {entry.category ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                            {entry.category}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            entry.priority === 'high'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : entry.priority === 'medium'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}
                        >
                          {entry.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs max-w-sm truncate">
                        {entry.description ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager
                page={mtPage}
                pagination={mtPagination}
                onPrev={() => setMtPage((p) => Math.max(0, p - 1))}
                onNext={() => setMtPage((p) => p + 1)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
